import {Box, Stack, Typography, TextField, Button, InputAdornment, Alert, Snackbar} from "@mui/material";
import {useMemo, useState, useEffect} from "react";
import EventsApi from "../api/Api";
import {
  DEFAULT_EVENT_CATEGORIES,
  DEFAULT_EVENT_CATEGORY_COLOR,
  getDefaultEventCategoryColor,
} from "../constants/eventCategories";

const FrecuenciaOptions = {
    never: "No se repite",
    weekly: "Semanal",
    monthly: "Mensual",
};

const FrecuenciaMapping = {
    "No se repite": "NO",
    "Semanal": "SEMANAL",
    "Mensual": "MENSUAL",
};

const Categories = {
    elegir: "Elegir categoría",
};

const fieldSx = {
  bgcolor: "background.paper",
  borderRadius: "6px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    "& fieldset": { borderColor: "black" },
    "&:hover fieldset": { borderColor: "black" },
    "&.Mui-focused fieldset": { borderColor: "black" },
  },
  "& .MuiInputBase-input": {
    py: 0.5,
    px: 0.5,
    fontSize: 13,
  },
  "& .MuiNativeSelect-select": {
    py: 0.5,
    px: 0.5,
    fontSize: 13,
  },
  "& textarea": {
    fontSize: 13,
  },
};

const labelSx = {
  fontSize: 13,
  color: "text.primary",
};

const formatTimeHM = (time) => {
  if (!time) return "";
  return String(time).slice(0, 5);
};

export default function AgregarEvento({onClose, onEventAdded, selectedDate, selectedEvent, onCancelEdit, onDeleteEvent, eventCategories = DEFAULT_EVENT_CATEGORIES}) {
  const [titulo, setTitulo] = useState("");
  const [categoria, setCategoria] = useState(Categories.elegir);
  const [fecha, setFecha] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFin, setHoraFin] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [repetibleValid, setRepetibleValid] = useState(FrecuenciaOptions.never);
  const [cantidadRepeticiones, setCantidadRepeticiones] = useState(0);
  const [lastFocused, setLastFocused] = useState("fecha");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const categoryOptions = useMemo(() => {
    const optionsByName = new Map();

    (eventCategories || []).forEach((item) => {
      const name = String(item?.name || "").trim();
      if (!name) return;
      optionsByName.set(name, {
        id: item.id,
        name,
        color: item.color || getDefaultEventCategoryColor(name),
        isHistorical: false,
      });
    });

    if (
      selectedEvent?.category &&
      !optionsByName.has(selectedEvent.category)
    ) {
      optionsByName.set(selectedEvent.category, {
        id: `historical-${selectedEvent.category}`,
        name: selectedEvent.category,
        color:
          selectedEvent.categoryColor ||
          selectedEvent.color ||
          getDefaultEventCategoryColor(selectedEvent.category),
        isHistorical: true,
      });
    }

    return Array.from(optionsByName.values());
  }, [eventCategories, selectedEvent]);

  const selectedCategory = useMemo(
    () => categoryOptions.find((option) => option.name === categoria) || null,
    [categoryOptions, categoria]
  );

  const selectedCategoryColor =
    categoria === Categories.elegir
      ? "#9e9e9e"
      : selectedCategory?.color || DEFAULT_EVENT_CATEGORY_COLOR;

  useEffect(() => {
    if (selectedEvent) {
      setTitulo(selectedEvent.titulo || "");
      setCategoria(selectedEvent.category || Categories.elegir);
      setFecha(selectedEvent.fecha || "");
      setFechaFin(selectedEvent.endDate || selectedEvent.fecha || "");
      setHoraInicio(formatTimeHM(selectedEvent.horaInicio));
      setHoraFin(formatTimeHM(selectedEvent.horaFin));
      setDescripcion(selectedEvent.description || "");
      const repText = Object.keys(FrecuenciaMapping).find(k => FrecuenciaMapping[k] === selectedEvent.repeatType) || FrecuenciaOptions.never;
      setRepetibleValid(repText);
    } else {
      handleClear();
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (!selectedEvent && selectedDate && selectedDate.date) {
      const year = selectedDate.date.getFullYear();
      const month = String(selectedDate.date.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      if (lastFocused === "fecha") {
        setFecha(dateStr);
        setFechaFin(dateStr);
      } else if (lastFocused === "horaFin") {
        setFechaFin(dateStr);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

    const ishorainiciovoid = horaInicio === "";
    const ishorafinvoid = horaFin === "";
    const timesValid = ishorainiciovoid === ishorafinvoid; // Ambos deben estar vacíos o ambos deben tener valor
    const buttonsDisabled =
        titulo.trim() === "" ||
        categoria === Categories.elegir ||
        fecha === "" ||
        descripcion.trim() === "" ||
        !timesValid ||
        loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const repType = FrecuenciaMapping[repetibleValid] || "NO";
      const reps = parseInt(cantidadRepeticiones, 10) || 0;
      const count = repType === "NO" ? 0 : reps;

      const [sy, sm, sd] = fecha.split("-").map(Number);
      const [ey, em, ed] = (fechaFin || fecha).split("-").map(Number);

      const baseStart = new Date(sy, sm - 1, sd);
      const baseEnd = new Date(ey, em - 1, ed);
      if (!ishorainiciovoid && !ishorafinvoid && horaFin < horaInicio && baseEnd <= baseStart) {
        baseEnd.setDate(baseEnd.getDate() + 1);
      }

      const eventsToSave = [];

      for (let i = 0; i <= count; i++) {
        const currentStart = new Date(baseStart);
        const currentEnd = new Date(baseEnd);

        if (repType === "SEMANAL") {
          currentStart.setDate(currentStart.getDate() + (i * 7));
          currentEnd.setDate(currentEnd.getDate() + (i * 7));
        } else if (repType === "MENSUAL") {
          currentStart.setMonth(currentStart.getMonth() + i);
          currentEnd.setMonth(currentEnd.getMonth() + i);
        }

        const formatDateStr = (d) => {
          const yyyy = d.getFullYear();
          const mmm = String(d.getMonth() + 1).padStart(2, '0');
          const ddd = String(d.getDate()).padStart(2, '0');
          return `${yyyy}-${mmm}-${ddd}`;
        };

        eventsToSave.push({
          scope: "EMPRESA",
          title: titulo,
          description: descripcion,
          category: categoria,
          categoryColor: selectedCategoryColor,
          startDate: formatDateStr(currentStart),
          endDate: formatDateStr(currentEnd),
          startTime: ishorainiciovoid ? undefined : horaInicio,
          endTime: ishorafinvoid ? undefined : horaFin,
          repeatType: repType,
          isActive: true,
        });
      }

      if (selectedEvent) {
        await EventsApi.update(selectedEvent.id, eventsToSave[0]);
        // Si se agregaron repeticiones extra en modo edición, crearlas como nuevos eventos
        for (let i = 1; i < eventsToSave.length; i++) {
          await EventsApi.create(eventsToSave[i]);
        }
        setSnackbar({ open: true, message: "Evento modificado con éxito", severity: "success" });
        if (onEventAdded) onEventAdded();
      } else {
        for (const evt of eventsToSave) {
          await EventsApi.create(evt);
        }
        setSnackbar({ open: true, message: `Evento${count > 0 ? 's' : ''} agregado${count > 0 ? 's' : ''} con éxito`, severity: "success" });
        handleClear();
        if (onEventAdded) onEventAdded();
      }
    } catch (error) {
      console.error("Error al guardar evento:", error);
      setSnackbar({ open: true, message: "Error al guardar el evento: " + (error.message || "Desconocido"), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setTitulo("");
    setCategoria(Categories.elegir);
    setFecha("");
    setFechaFin("");
    setHoraInicio("");
    setHoraFin("");
    setDescripcion("");
    setRepetibleValid(FrecuenciaOptions.never);
    setCantidadRepeticiones(0);
    setLastFocused("fecha");
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        bgcolor: "background.paper",
        borderRadius: "22px",
        p: 1.5,
        overflowY: "auto",
      }}
    >
      <Stack spacing={1}>
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
          {selectedEvent ? "Modificar evento" : "Agregar nuevo evento"}
        </Typography>

        <Box>
          <Typography sx={labelSx}>Título</Typography>
          <TextField
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            required
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </Box>

        <Stack direction="row" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={labelSx}>Categoría</Typography>
            <TextField
              select
              SelectProps={{ native: true }}
              fullWidth
              size="small"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              sx={{
                ...fieldSx,
                "& .MuiNativeSelect-select": {
                  ...fieldSx["& .MuiNativeSelect-select"],
                  backgroundColor: `${selectedCategoryColor}33`,
                  color: selectedCategoryColor,
                  fontWeight: "bold",
                  borderRadius: "6px",
                },
              }}
            >
              <option value={Categories.elegir}>{Categories.elegir}</option>
              {categoryOptions.map((category) => (
                <option key={category.id || category.name} value={category.name}>
                  {category.isHistorical ? `${category.name} (eliminada)` : category.name}
                </option>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={labelSx}>Fecha</Typography>
            <TextField
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={fecha}
              onFocus={() => setLastFocused("fecha")}
              onChange={(e) => {
                setFecha(e.target.value);
                setFechaFin(e.target.value);
              }}
              sx={fieldSx}
            />
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={labelSx}>Hora inicio</Typography>
            <TextField
              type="time"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
              value={horaInicio}
              onChange={(e) => setHoraInicio(formatTimeHM(e.target.value))}
              sx={fieldSx}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={labelSx}>Hora fin</Typography>
            <TextField
              type="time"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
              value={horaFin}
              onFocus={() => setLastFocused("horaFin")}
              onChange={(e) => setHoraFin(formatTimeHM(e.target.value))}
              sx={fieldSx}
            />
          </Box>
        </Stack>

        <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
          Si no tiene hora inicio ni fin, es evento de todo el día
        </Typography>

        <Box>
          <Typography sx={labelSx}>Descripción</Typography>
          <TextField
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            fullWidth
            multiline
            rows={4}
            sx={{
              ...fieldSx,
              "& .MuiOutlinedInput-root": {
                alignItems: "flex-start",
                p: 0,
              },
              "& .MuiInputBase-inputMultiline": {
                p: "6px 7px",
                lineHeight: 1.25,
              },
            }}
          />
        </Box>

        <Stack direction="row" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={labelSx}>Repetición</Typography>
            <TextField
              select
              SelectProps={{ native: true }}
              fullWidth
              size="small"
              value={repetibleValid}
              onChange={(e) => setRepetibleValid(e.target.value)}
              sx={fieldSx}
            >
              {Object.entries(FrecuenciaOptions).map(([key, value]) => (
                <option key={key} value={value}>
                  {value}
                </option>
              ))}
            </TextField>
          </Box>
          {FrecuenciaMapping[repetibleValid] !== "NO" && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={labelSx}>Cantidad</Typography>
              <TextField
                type="number"
                InputProps={{
                  endAdornment: FrecuenciaMapping[repetibleValid] === "SEMANAL"
                    ? <InputAdornment position="end">semanas</InputAdornment>
                    : <InputAdornment position="end">meses</InputAdornment>,
                }}
                fullWidth
                size="small"
                value={cantidadRepeticiones}
                onChange={(e) => setCantidadRepeticiones(e.target.value)}
                sx={fieldSx}
              />
            </Box>
          )}
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ pt: 2 }}>
          {selectedEvent ? (
            <Button color="error" size="small" onClick={() => onDeleteEvent(selectedEvent)} sx={{ textTransform: "none", fontSize: 14 }}>
              Eliminar
            </Button>
          ) : (
            <Button size="small" onClick={handleClear} sx={{ textTransform: "none", fontSize: 14 }}>
              Limpiar campos
            </Button>
          )}
          <Button disabled={buttonsDisabled} type="submit" variant="contained" color="primary" size="small" sx={{ textTransform: "none", fontSize: 14 }}>
            {loading ? "Cargando..." : (selectedEvent ? "Guardar cambios" : "Guardar evento")}
          </Button>
        </Stack>
      </Stack>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
